import React from 'react';
import { Badge, Avatar, Button, Dropdown, Typography, Space, Popover, List, Empty, theme } from 'antd';
import {
    BellOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    LogoutOutlined,
    CheckOutlined,
} from '@ant-design/icons';
import { useApp } from '../context/AppContext';
import { View } from '../constants/roles';
import { AnimatedThemeToggler } from './AnimatedThemeToggler';
import { formatDdMmYyyyTime } from "../utils/formatDdMmYyyy";

const { Text } = Typography;

/**
 * Top header bar with context label, notification bell, and sign-out.
 * Now powered by Ant Design components.
 */
const NOTIF_ICON_MAP = {
    REVIEW_REQUESTED: { bg: 'var(--color-accent-tint)', color: 'var(--color-accent)', icon: '📋' },
    CHANGES_REQUESTED: { bg: 'var(--color-warn-bg)', color: 'var(--color-warn-fg)', icon: '✏️' },
    PROFILE_APPROVED: { bg: 'var(--color-ok-bg)', color: 'var(--color-ok-fg)', icon: '✅' },
    PROFILE_FINALIZED: { bg: 'var(--color-info-bg)', color: 'var(--color-info-fg)', icon: '🔒' },
    FIELD_CHANGED: { bg: 'var(--color-warn-bg)', color: 'var(--color-warn-fg)', icon: '🔄' },
    CONTRACT_COMMENT: { bg: 'var(--color-accent-tint)', color: 'var(--color-accent)', icon: '💬' },
    SCOPING_REVIEW_REQUESTED: { bg: 'var(--color-accent-tint)', color: 'var(--color-accent)', icon: '📋' },
    SCOPING_CHANGES_REQUESTED: { bg: 'var(--color-warn-bg)', color: 'var(--color-warn-fg)', icon: '✏️' },
    SCOPING_BACK_TO_DRAFT: { bg: 'var(--color-warn-bg)', color: 'var(--color-warn-fg)', icon: '↩️' },
    SCOPING_APPROVED: { bg: 'var(--color-ok-bg)', color: 'var(--color-ok-fg)', icon: '✅' },
    SCOPING_FINALIZED: { bg: 'var(--color-info-bg)', color: 'var(--color-info-fg)', icon: '🔒' },
    MEETING_DELEGATES_REQUESTED: { bg: 'var(--color-accent-tint)', color: 'var(--color-accent)', icon: '👥' },
    MEETING_REQUEST_UPDATED: { bg: 'var(--color-accent-tint)', color: 'var(--color-accent)', icon: '📝' },
    MEETING_SCHEDULED: { bg: 'var(--color-accent-tint)', color: 'var(--color-accent)', icon: '📅' },
    MEETING_RESCHEDULED: { bg: 'var(--color-warn-bg)', color: 'var(--color-warn-fg)', icon: '📅' },
};

const Topbar = () => {
    const {
        currentView,
        activeProject,
        projects,
        notifications,
        markNotificationRead,
        markAllNotificationsRead,
        selectProject,
        setCurrentView,
        sidebarOpen,
        toggleSidebar,
        signOut,
    } = useApp();

    const { token } = theme.useToken();

    const unreadCount = notifications.filter((n) => !n.read).length;

    const handleNotificationClick = (n) => {
        markNotificationRead(n.id);
        if (n.projectId) {
            const targetProject = projects.find((p) => p.id === n.projectId);
            if (targetProject) {
                selectProject(targetProject);
            }
        }
        if (n.targetView === 'MEETINGS') {
            setCurrentView(View.MEETINGS);
        }
    };

    const notificationContent = (
        <div style={{ width: 360 }}>
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0 0 12px', borderBottom: '1px solid var(--color-border)', marginBottom: 8,
            }}>
                <Text strong style={{ fontSize: 14 }}>Communication Hub</Text>
                {unreadCount > 0 && (
                    <Button size="small" type="link" onClick={markAllNotificationsRead} icon={<CheckOutlined />}>
                        Mark all read
                    </Button>
                )}
            </div>
            {notifications.length === 0 ? (
                <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="No active transmissions"
                    style={{ padding: '20px 0' }}
                />
            ) : (
                <List
                    dataSource={notifications}
                    renderItem={(n) => {
                        const style = NOTIF_ICON_MAP[n.type] || { bg: 'var(--color-bg-alt)', color: 'var(--color-text-muted-dark)', icon: '🔔' };
                        return (
                            <List.Item
                                onClick={() => handleNotificationClick(n)}
                                style={{
                                    cursor: 'pointer',
                                    padding: '10px 8px',
                                    borderRadius: 10,
                                    opacity: n.read ? 0.55 : 1,
                                    background: n.read ? 'transparent' : 'var(--color-bg-base)',
                                    marginBottom: 2,
                                    transition: 'all 0.2s',
                                }}
                            >
                                <List.Item.Meta
                                    avatar={
                                        <div style={{
                                            width: 32, height: 32, borderRadius: 10,
                                            background: style.bg,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: 15,
                                        }}>
                                            {style.icon}
                                        </div>
                                    }
                                    title={<Text style={{ fontSize: 12, fontWeight: 700 }}>{n.title || 'Notification'}</Text>}
                                    description={
                                        <div>
                                            <Text style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{n.text}</Text>
                                            <br />
                                            <Text type="secondary" style={{ fontSize: 10 }}>
                                                {formatDdMmYyyyTime(n.timestamp)}
                                            </Text>
                                        </div>
                                    }
                                />
                            </List.Item>
                        );
                    }}
                />
            )}
        </div>
    );

    return (
        <header data-testid="app-topbar" style={{
            flexShrink: 0,
            minHeight: 64,
            flexWrap: 'wrap',
            rowGap: 8,
            background: 'var(--color-bg-white)',
            borderBottom: '1px solid var(--color-border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '8px 24px',
            position: 'sticky',
            top: 0,
            zIndex: 40,
        }}>
            <Space align="center" size={16}>
                <Button
                    type="text"
                    icon={sidebarOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
                    onClick={toggleSidebar}
                    style={{ fontSize: 18 }}
                />
                <div>
                    <Text type="secondary" style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--color-text-muted)' }}>
                        {activeProject ? 'Active Engagement' : 'Authorized Portal'}
                    </Text>
                    <br />
                    <Text strong style={{ fontSize: 15, color: 'var(--color-text-hero)' }}>
                        {activeProject
                            ? activeProject.companyName
                            : currentView === View.LEAD_ADVISORS
                              ? 'Lead Advisors'
                              : currentView === View.ADMINISTRATORS
                                ? 'Administrators'
                              : 'Strategic GRC Console'}
                    </Text>
                </div>
            </Space>

            <Space size={12}>
                <AnimatedThemeToggler style={{ color: 'var(--color-text-hero)' }} />
                <Popover
                    content={notificationContent}
                    trigger="click"
                    placement="bottomRight"
                    arrow={false}
                >
                    <Badge count={unreadCount} size="small" offset={[-2, 2]}>
                        <Button
                            type="text"
                            icon={<BellOutlined style={{ fontSize: 20 }} />}
                            style={{ width: 40, height: 40 }}
                        />
                    </Badge>
                </Popover>

                <div style={{ width: 1, height: 24, background: 'var(--color-border)' }} />

                <Button
                    type="text"
                    icon={<LogoutOutlined />}
                    onClick={signOut}
                    style={{ color: 'var(--color-text-muted)' }}
                >
                    Sign Out
                </Button>
            </Space>
        </header>
    );
};

export default Topbar;
